import {useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import {useReportAccess} from "../AccessContext";
import {deslugify, parsePdfPosition, pdfPositionQuery} from "../contentUtils";
import {useContentParams} from "../router";
import {useContentTitle} from "../useContentTitle";
import {PdfDocumentBody} from "./PdfDocumentBody";
import {PdfTableOfContents} from "./PdfTableOfContents";
import type {PdfViewerHandle} from "./PdfViewer";
import layoutClasses from "../ContentLayout.module.css";
import articleClasses from "../HtmlArticle.module.css";
import contentBaseClasses from "../contentBase.module.css";
import classes from "./PdfResource.module.css";

/**
 * Standalone PDF topic page in the HTML topic's layout (TOC | article |
 * related): the topic's name as heading and document title (metadata
 * endpoint, else the URL slug), the chrome-less viewer as the article body,
 * and admin's table of contents in the TOC column when the PDF has one.
 * ContentPage remounts the leaf per topic, so no self-keying.
 */
export const PdfResource = ({
    resourceId,
    onShown,
}: {
    resourceId: string;
    /** Called when the document itself has loaded (not just its metadata). */
    onShown?: () => void;
}) => {
    const {t} = useTranslation();
    const {topicName} = useContentParams();
    // The position to show (`?page=&y=`): a search hit's, or the last TOC click's.
    const [searchParams, setSearchParams] = useSearchParams();
    const position = parsePdfPosition(searchParams);
    const {data: meta, isLoading: isMetaLoading} =
        dibAppApi.endpoints.getPdfMetadata.useQuery({resourceId});
    const {data: toc} = dibAppApi.endpoints.getPdfToc.useQuery({resourceId});
    const viewerRef = useRef<PdfViewerHandle>(null);
    const [loadedPageCount, setLoadedPageCount] = useState<number | null>(null);

    // Access verdict → ContentPage's NoAccessModal, like the HTML leaves.
    useReportAccess(resourceId, meta?.access);

    // Name: the metadata endpoint, else the URL slug — keeps the page usable
    // while dib-app-api lacks the endpoint.
    const name = meta?.name ?? (isMetaLoading ? undefined : deslugify(topicName));
    useContentTitle(name);

    const outline = toc && toc.selectedSource !== "none" ? toc.outline : [];
    const pageCountMismatch =
        toc?.pageCount != null &&
        loadedPageCount !== null &&
        toc.pageCount !== loadedPageCount;
    if (pageCountMismatch) {
        console.warn(
            `PDF TOC saved for ${toc!.pageCount} pages, document has ${loadedPageCount}; hiding the table of contents`
        );
    }
    const showToc = outline.length > 0 && !pageCountMismatch;

    return (
        <div className={layoutClasses.page}>
            {/* Always rendered, like the HTML page's, so the column geometry
                is the same with and without a table of contents. */}
            <nav
                className={layoutClasses.toc}
                aria-label={t("Title.LabelOfContents", {defaultValue: "Table of contents"})}
            >
                {showToc && (
                    <PdfTableOfContents
                        outline={outline}
                        position={position}
                        onNavigate={(entry) => {
                            // Jump directly too: clicking the entry the URL already
                            // holds leaves the URL unchanged, so nothing else would.
                            viewerRef.current?.goToBookmark(entry.pageIndex, entry.y);
                            // Replace: TOC clicks don't stack up in the back history.
                            setSearchParams(pdfPositionQuery(entry), {replace: true});
                        }}
                    />
                )}
            </nav>
            <article
                className={`${layoutClasses.article} ${contentBaseClasses.contentBase} ${classes.article}`}
            >
                <h1 className={articleClasses.title}>{name ?? " "}</h1>
                {/* Always rendered, as on the HTML page: the divider under the
                    title (contentBase.module.css .desccontainer). */}
                <div className="desccontainer" />
                <PdfDocumentBody
                    topicId={resourceId}
                    viewerRef={viewerRef}
                    // The TOC jumps to positions within a page.
                    enableBookmark
                    position={position}
                    onDocumentLoad={({pageCount}) => {
                        setLoadedPageCount(pageCount);
                        onShown?.();
                    }}
                />
            </article>
            {/* Empty: keeps the right column so the article sits where the
                HTML page's does. */}
            <aside className={layoutClasses.related} />
        </div>
    );
};
